/**
 * Front-end typewriter. Every constant below is the React reference value —
 * src/features/Home/Hero/useTypewriter.ts (initial 600, type 60, hold 2500,
 * delete 30, gap 400) and src/features/Home/Hero/useIsMobile.ts (the query).
 */
const INITIAL_DELAY_MS = 600;
const TYPE_SPEED_MS = 60;
const DELETE_SPEED_MS = 30;
const HOLD_MS = 2500;
const PHRASE_GAP_MS = 400;
const MOBILE_QUERY = '(max-width: 768px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const FALLBACK_PLACEHOLDER = 'Enter your topic...';

function initTypewriterInput( block ) {
	let phrases = [];
	try {
		phrases = JSON.parse( block.dataset.phrases || '[]' );
	} catch {
		phrases = [];
	}
	phrases = Array.isArray( phrases ) ? phrases.filter( Boolean ) : [];

	const input = block.querySelector( '.input-typewriter-field' );
	if ( ! input || phrases.length === 0 ) {
		return;
	}

	// Reduced motion (T-085): nothing types or cycles. The saved placeholder is
	// the first phrase in full (save.js), so leaving the field as served is the
	// static state, with no frame where the box is empty.
	if ( window.matchMedia( REDUCED_MOTION_QUERY ).matches ) {
		return;
	}

	const mobile = window.matchMedia( MOBILE_QUERY );

	let timeoutId;
	let phraseIndex = 0;
	let charIndex = 0;
	let isDeleting = false;
	let isAnimating = false;
	let userStopped = false;

	// React drives the field through `defaultValue`, i.e. the value ATTRIBUTE,
	// which keeps the element's dirty flag clear. Setting the .value property
	// instead would mark it dirty and freeze the display.
	const write = ( text ) => input.setAttribute( 'value', text );

	const tick = () => {
		const phrase = phrases[ phraseIndex ];

		if ( ! isDeleting ) {
			charIndex += 1;
			write( phrase.slice( 0, charIndex ) );

			if ( charIndex === phrase.length ) {
				isDeleting = true;
				timeoutId = setTimeout( tick, HOLD_MS );
				return;
			}

			timeoutId = setTimeout( tick, TYPE_SPEED_MS );
			return;
		}

		charIndex -= 1;
		write( phrase.slice( 0, charIndex ) );

		if ( charIndex === 0 ) {
			isDeleting = false;
			phraseIndex = ( phraseIndex + 1 ) % phrases.length;
			timeoutId = setTimeout( tick, PHRASE_GAP_MS );
			return;
		}

		timeoutId = setTimeout( tick, DELETE_SPEED_MS );
	};

	const start = () => {
		if ( isAnimating || userStopped ) {
			return;
		}
		isAnimating = true;
		phraseIndex = 0;
		charIndex = 0;
		isDeleting = false;
		input.readOnly = true;
		input.placeholder = '';
		write( '' );
		timeoutId = setTimeout( tick, INITIAL_DELAY_MS );
	};

	// Hands the field back to the visitor: the typewriter is off, the
	// placeholder is the plain prompt. Same end state as HeroInput's
	// non-animating branch.
	const stop = () => {
		isAnimating = false;
		clearTimeout( timeoutId );
		input.readOnly = false;
		input.removeAttribute( 'value' );
		input.value = '';
		input.placeholder = FALLBACK_PLACEHOLDER;
	};

	// User focusing the field takes over from the animation, mirroring
	// HeroInput's stop-on-focus behavior.
	input.addEventListener( 'focus', () => {
		if ( ! isAnimating ) {
			return;
		}
		userStopped = true;
		stop();
	} );

	// useIsMobile: no typewriter at or below 768 px, re-evaluated on resize.
	mobile.addEventListener( 'change', () => {
		if ( mobile.matches ) {
			stop();
		} else {
			start();
		}
	} );

	if ( mobile.matches ) {
		stop();
	} else {
		start();
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	document
		.querySelectorAll( '.wp-block-avoid-content-input-typewriter' )
		.forEach( initTypewriterInput );
} );
